import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProjectService from '../../services/ProjectService';
import ProjectEstimatesService from '../../services/ProjectEstimatesService';
import ProjectScheduleService from '../../services/ProjectScheduleService';
import ProjectTeamService from '../../services/ProjectTeamService';
import ProjectTeamScheduleService from '../../services/ProjectTeamScheduleService';
import PayInService from '../../services/PayInService';
import PayoutService from '../../services/PayoutService';
import PettyCashService from '../../services/PettyCashService';
import utility from '../../utils/utility';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const ProjectDetails = () => {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [projectEstimates, setProjectEstimates] = useState([]);
    const [projectSchedule, setProjectSchedule] = useState([]);
    const [projectTeam, setProjectTeam] = useState([]);
    const [teamSchedule, setTeamSchedule] = useState([]);
    const [payIn, setPayIn] = useState([]);
    const [payOut, setPayOut] = useState([]);
    const [pettyCash, setPettyCash] = useState([]);
    const [allPayouts, setAllPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('summary');
    const [pettyCashSubTab, setPettyCashSubTab] = useState('all-payouts');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchEstimates, setSearchEstimates] = useState('');
    const [searchSchedule, setSearchSchedule] = useState('');
    const [searchTeam, setSearchTeam] = useState('');
    const [searchTeamSchedule, setSearchTeamSchedule] = useState('');
    const [searchPayIn, setSearchPayIn] = useState('');
    const [searchPayOut, setSearchPayOut] = useState('');
    const [searchPettyCash, setSearchPettyCash] = useState('');

    const filteredProjectEstimates = useMemo(() => filterList(projectEstimates, searchEstimates, ['memberName', 'projectName', 'expenditureTypeName', 'remunerationType', 'status']), [projectEstimates, searchEstimates]);
    const filteredProjectSchedule = useMemo(() => filterList(projectSchedule, searchSchedule, ['activity', 'projectName', 'status']), [projectSchedule, searchSchedule]);
    const filteredProjectTeam = useMemo(() => filterList(projectTeam, searchTeam, ['memberName', 'projectName', 'roleName', 'remunerationType', 'status']), [projectTeam, searchTeam]);
    const filteredTeamSchedule = useMemo(() => filterList(teamSchedule, searchTeamSchedule, ['projectName', 'memberName', 'status']), [teamSchedule, searchTeamSchedule]);
    const filteredPayIn = useMemo(() => filterList(payIn, searchPayIn, ['paymentMode', 'transactionNo', 'projectName', 'branchName', 'staffName', 'memberName', 'vendorName', 'status']), [payIn, searchPayIn]);
    const filteredPayOut = useMemo(() => filterList(payOut, searchPayOut, ['paymentMode', 'transactionNo', 'projectName', 'branchName', 'staffName', 'memberName', 'vendorName', 'status']), [payOut, searchPayOut]);
    const filteredPettyCash = useMemo(() => filterList(pettyCash, searchPettyCash, ['paymentMode', 'transactionNo', 'projectName', 'branchName', 'staffName', 'memberName', 'vendorName', 'status']), [pettyCash, searchPettyCash]);

    // Helper functions for grouping
    const groupByDate = (data) => {
        const grouped = {};
        data.forEach(item => {
            const date = item.paymentDate ? item.paymentDate.split('T')[0] : 'No Date';
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(item);
        });
        return Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a)).reduce((acc, key) => {
            acc[key] = grouped[key];
            return acc;
        }, {});
    };

    const groupByEntity = (data) => {
        const grouped = {};
        data.forEach(item => {
            const entity = item.staffName || item.memberName || item.vendorName || 'Unknown';
            if (!grouped[entity]) grouped[entity] = [];
            grouped[entity].push(item);
        });
        return Object.keys(grouped).sort().reduce((acc, key) => {
            acc[key] = grouped[key];
            return acc;
        }, {});
    };

    const groupByExpenditure = (data) => {
        const grouped = {};
        data.forEach(item => {
            const type = item.expenditureTypeName || 'Uncategorized';
            if (!grouped[type]) grouped[type] = [];
            grouped[type].push(item);
        });
        return Object.keys(grouped).sort().reduce((acc, key) => {
            acc[key] = grouped[key];
            return acc;
        }, {});
    };

    const calculateGroupTotal = (items) => {
        return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const projectData = await ProjectService.getById(id);
            setProject(projectData.data);

            const [estimates, schedule, team, teamSch, payin, payout, petty] = await Promise.all([
                ProjectEstimatesService.getAll(1, 100, { projectId: id }),
                ProjectScheduleService.getAll(1, 100, { projectId: id }),
                ProjectTeamService.getAll(1, 100, { projectId: id }),
                ProjectTeamScheduleService.getAll(1, 100, { projectId: id }),
                PayInService.getAllPayIn(1, 100, { projectId: id }),
                PayoutService.getAllPayouts(1, 100, { projectId: id }),
                PettyCashService.getAllPettyCash(1, 1000, { projectId: id })
            ]);

            setProjectEstimates(estimates.data || []);
            setProjectSchedule(schedule.data || []);
            setProjectTeam(team.data || []);
            setTeamSchedule(teamSch.data || []);
            setPayIn(payin.data || []);
            setPayOut(payout.data || []);
            setPettyCash(petty.data || []);
            setAllPayouts(petty.data || []);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = () => {
        const totalEstimatedCost = project?.estimatedProjectCost || 0;
        const totalEstimatedReturns = project?.estimatedReturns || 0;
        const totalProjectEstimates = projectEstimates.reduce((sum, est) => sum + (parseFloat(est.totalAmount) || 0), 0);
        const totalPayIn = payIn.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const totalPayOut = payOut.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const totalPettyCash = pettyCash.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const totalExpenses = totalPayOut;
        const remainingBudget = totalProjectEstimates - totalExpenses;
        const netProfit = totalPayIn - totalExpenses;

        return {
            totalEstimatedCost,
            totalEstimatedReturns,
            totalProjectEstimates,
            totalPayIn,
            totalPayOut,
            totalPettyCash,
            totalExpenses,
            remainingBudget,
            netProfit
        };
    };

    if (loading) {
        return <div className="container-fluid py-2"><div className="text-center">Loading...</div></div>;
    }

    if (!project) {
        return <div className="container-fluid py-2"><div className="text-center">Project not found.</div></div>;
    }

    const summary = calculateSummary();

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 mb-4">
                    <Link to="/cpnl/project" className="btn btn-outline-dark mb-3">
                        <i className="material-symbols-rounded text-sm">arrow_back</i>&nbsp;&nbsp;Back to Projects
                    </Link>
                </div>
            </div>

            {/* Project Header */}
            <div className="row">
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Project Details: {project.name}</h6>
                            </div>
                        </div>
                        <div className="card-body px-4 pb-2">
                            <div className="row">
                                <div className="col-md-6">
                                    <p><strong>Project Type:</strong> {project.projectTypeName || 'N/A'}</p>
                                    <p><strong>Status:</strong> {project.status || 'Active'}</p>
                                    <p><strong>Estimated Cost:</strong> ₹{project.estimatedProjectCost?.toLocaleString() || 0}</p>
                                </div>
                                <div className="col-md-6">
                                    <p><strong>Start Date:</strong> {project.startDate ? project.startDate.split('T')[0] : 'N/A'}</p>
                                    <p><strong>End Date:</strong> {project.endDate ? project.endDate.split('T')[0] : 'N/A'}</p>
                                    <p><strong>Estimated Returns:</strong> ₹{project.estimatedReturns?.toLocaleString() || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="row">
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header border-bottom">
                            <ul className="nav nav-tabs" role="tablist">
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')} role="tab">
                                        <strong>Summary</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'estimates' ? 'active' : ''}`} onClick={() => setActiveTab('estimates')} role="tab">
                                        <strong>Estimates</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')} role="tab">
                                        <strong>Schedule</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'team' ? 'active' : ''}`} onClick={() => setActiveTab('team')} role="tab">
                                        <strong>Team</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'teamSchedule' ? 'active' : ''}`} onClick={() => setActiveTab('teamSchedule')} role="tab">
                                        <strong>Team Schedule</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'payIn' ? 'active' : ''}`} onClick={() => setActiveTab('payIn')} role="tab">
                                        <strong>Pay In</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'payOut' ? 'active' : ''}`} onClick={() => setActiveTab('payOut')} role="tab">
                                        <strong>Pay Out</strong>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className={`nav-link ${activeTab === 'pettyCash' ? 'active' : ''}`} onClick={() => setActiveTab('pettyCash')} role="tab">
                                        <strong>Petty Cash</strong>
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="card-body">
                            {/* Summary Tab */}
                            {activeTab === 'summary' && (
                                <div>
                                    <h6 className="mb-4">Financial Summary Report</h6>
                                    <div className="row">
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Initial Project Cost</p>
                                                    <h5 className="card-title text-primary">₹{summary.totalEstimatedCost?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Total Project Estimates</p>
                                                    <h5 className="card-title text-info">₹{summary.totalProjectEstimates?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Estimated Returns</p>
                                                    <h5 className="card-title text-success">₹{summary.totalEstimatedReturns?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Total Pay In (Income)</p>
                                                    <h5 className="card-title text-success">₹{summary.totalPayIn?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Total Pay Out</p>
                                                    <h5 className="card-title text-danger">₹{summary.totalPayOut?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Total Petty Cash</p>
                                                    <h5 className="card-title text-warning">₹{summary.totalPettyCash?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Total Expenses</p>
                                                    <h5 className="card-title text-danger">₹{summary.totalExpenses?.toLocaleString()}</h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Remaining Budget</p>
                                                    <h5 className={`card-title ${summary.remainingBudget >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        ₹{summary.remainingBudget?.toLocaleString()}
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 col-lg-4 mb-3">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <p className="text-muted mb-1">Net Profit/Loss</p>
                                                    <h5 className={`card-title ${summary.netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        ₹{summary.netProfit?.toLocaleString()}
                                                    </h5>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Project Estimates Tab */}
                            {activeTab === 'estimates' && (
                                <div>
                                    <h6 className="mb-3">Project Estimates</h6>
                                    <FilterBar
                                        value={searchEstimates}
                                        onChange={setSearchEstimates}
                                        onClear={() => setSearchEstimates('')}
                                        placeholder="Search project estimates..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Member</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Expenditure Type</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Days</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Remuneration Type</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Amount</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total Amount</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProjectEstimates.length === 0 ? (
                                                    <tr><td colSpan="7" className="text-center py-4">No estimates found.</td></tr>
                                                ) : (
                                                    filteredProjectEstimates.map((estimate) => (
                                                        <tr key={estimate.id}>
                                                            <td><p className="text-xs font-weight-bold mb-0">{estimate.memberName || 'Unknown'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{estimate.expenditureTypeName || 'Unknown'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{estimate.days || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{estimate.remunerationType || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">₹{parseFloat(estimate.remunerationAmount || 0).toLocaleString()}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">₹{parseFloat(estimate.totalAmount || 0).toLocaleString()}</p></td>
                                                            <td className="text-center"><span className={`badge badge-sm bg-gradient-${estimate.status === 'active' ? 'success' : 'secondary'}`}>{estimate.status}</span></td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Project Schedule Tab */}
                            {activeTab === 'schedule' && (
                                <div>
                                    <h6 className="mb-3">Project Schedule</h6>
                                    <FilterBar
                                        value={searchSchedule}
                                        onChange={setSearchSchedule}
                                        onClear={() => setSearchSchedule('')}
                                        placeholder="Search project schedules..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Activity</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Start Date</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">End Date</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProjectSchedule.length === 0 ? (
                                                    <tr><td colSpan="4" className="text-center py-4">No schedule found.</td></tr>
                                                ) : (
                                                    filteredProjectSchedule.map((schedule) => (
                                                        <tr key={schedule.id}>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.activity || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.date ? schedule.date.split('T')[0] : 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.endDate ? schedule.endDate.split('T')[0] : 'N/A'}</p></td>
                                                            <td className="text-center"><span className="badge badge-sm bg-gradient-info">{schedule.status || 'Pending'}</span></td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Project Team Tab */}
                            {activeTab === 'team' && (
                                <div>
                                    <h6 className="mb-3">Project Team</h6>
                                    <FilterBar
                                        value={searchTeam}
                                        onChange={setSearchTeam}
                                        onClear={() => setSearchTeam('')}
                                        placeholder="Search project team..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Member</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Role</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProjectTeam.length === 0 ? (
                                                    <tr><td colSpan="3" className="text-center py-4">No team members found.</td></tr>
                                                ) : (
                                                    filteredProjectTeam.map((team) => (
                                                        <tr key={team.id}>
                                                            <td><p className="text-xs font-weight-bold mb-0">{team.memberName || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{team.roleName || 'N/A'}</p></td>
                                                            <td className="text-center"><span className="badge badge-sm bg-gradient-success">{team.status || 'Active'}</span></td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Team Schedule Tab */}
                            {activeTab === 'teamSchedule' && (
                                <div>
                                    <h6 className="mb-3">Team Schedule</h6>
                                    <FilterBar
                                        value={searchTeamSchedule}
                                        onChange={setSearchTeamSchedule}
                                        onClear={() => setSearchTeamSchedule('')}
                                        placeholder="Search team schedule..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Member</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Task</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Date</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">End Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredTeamSchedule.length === 0 ? (
                                                    <tr><td colSpan="4" className="text-center py-4">No team schedule found.</td></tr>
                                                ) : (
                                                    filteredTeamSchedule.map((schedule) => (
                                                        <tr key={schedule.id}>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.memberName || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.activity || 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.availableDate ? schedule.availableDate.split('T')[0] : 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{schedule.availableEndDate ? schedule.availableEndDate.split('T')[0] : 'N/A'}</p></td>

                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Pay In Tab */}
                            {activeTab === 'payIn' && (
                                <div>
                                    <h6 className="mb-3">Pay In Transactions</h6>
                                    <FilterBar
                                        value={searchPayIn}
                                        onChange={setSearchPayIn}
                                        onClear={() => setSearchPayIn('')}
                                        placeholder="Search pay-ins..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Amount</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Entity / Project</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPayIn.length === 0 ? (
                                                    <tr><td colSpan="5" className="text-center py-4">No pay in records found.</td></tr>
                                                ) : (
                                                    filteredPayIn.map((pay) => (
                                                        <tr key={pay.id}>
                                                            <td><p className="text-xs font-weight-bold mb-0 px-3">{pay.paymentDate ? pay.paymentDate.split('T')[0] : 'N/A'}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">₹{parseFloat(pay.amount || 0).toLocaleString()}</p></td>
                                                            <td><p className="text-xs font-weight-bold mb-0">{pay.paymentMode || 'N/A'}</p></td>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0">{pay.staffName || pay.memberName || pay.vendorName || 'N/A'}</p>
                                                                <p className="text-xs text-secondary mb-0">{pay.projectName || 'No Project'}</p>
                                                            </td>
                                                            <td className="text-center"><span className={`badge badge-sm bg-gradient-${pay.isActive ? 'success' : 'secondary'}`}>{pay.isActive ? 'Active' : 'Inactive'}</span></td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Pay Out Tab */}
                            {activeTab === 'payOut' && (
                                <div>
                                    <h6 className="mb-3">Pay Out Transactions</h6>
                                    <FilterBar
                                        value={searchPayOut}
                                        onChange={setSearchPayOut}
                                        onClear={() => setSearchPayOut('')}
                                        placeholder="Search pay-outs..."
                                    />
                                    <div className="table-responsive">
                                        <table className="table table-bordered align-items-center mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paied By</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Paid to Entity / Project</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Exp. Type</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Amount (₹)</th>
                                                    <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                    <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPayOut.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">No payout transactions found.</td>
                                                    </tr>
                                                ) : (
                                                    filteredPayOut.map((payout) => (
                                                        <tr key={payout.id}>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0 px-3">{payout.paymentDate ? payout.paymentDate.split('T')[0] : 'N/A'}</p>
                                                            </td>


                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0">
                                                                    {payout.createdByName || 'N/A'}
                                                                </p>
                                                            </td>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0">
                                                                    {payout.staffName || payout.memberName || payout.vendorName || 'N/A'}
                                                                </p>
                                                                <p className="text-xs text-secondary mb-0">{payout.projectName || 'No Project'}</p>
                                                            </td>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0">{payout.expenditureTypeName || 'N/A'}</p>
                                                                <p className="text-xs text-secondary mb-0">{payout.remarks || ''}</p>
                                                            </td>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0">{payout.paymentMode || 'N/A'}</p>
                                                                <p className="text-xs text-secondary mb-0">{payout.transactionNo || ''}</p>
                                                            </td>
                                                            <td>
                                                                <p className="text-xs font-weight-bold mb-0 text-end">{payout.amount || '0.00'}</p>
                                                            </td>
                                                            <td>
                                                                <span className={`badge badge-sm bg-gradient-${payout.isActive ? 'success' : 'secondary'} cursor-pointer`}>
                                                                    {payout.isActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>

                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>

                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Petty Cash Tab */}
                            {activeTab === 'pettyCash' && (
                                <div>
                                    <h6 className="mb-3">Petty Cash Management</h6>

                                    {/* Sub-tabs for Petty Cash */}
                                    <ul className="nav nav-tabs bg-light rounded-top px-3 mb-0" role="tablist">
                                        <li className="nav-item">
                                            <a className={`nav-link ${pettyCashSubTab === 'all-payouts' ? 'active' : ''}`} onClick={() => setPettyCashSubTab('all-payouts')} role="tab">
                                                <strong>All Payouts</strong>
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a className={`nav-link ${pettyCashSubTab === 'group-date' ? 'active' : ''}`} onClick={() => setPettyCashSubTab('group-date')} role="tab">
                                                <strong>Group by Date</strong>
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a className={`nav-link ${pettyCashSubTab === 'group-entity' ? 'active' : ''}`} onClick={() => setPettyCashSubTab('group-entity')} role="tab">
                                                <strong>Group by Entity</strong>
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a className={`nav-link ${pettyCashSubTab === 'group-type' ? 'active' : ''}`} onClick={() => setPettyCashSubTab('group-type')} role="tab">
                                                <strong>Group by Exp. Type</strong>
                                            </a>
                                        </li>
                                    </ul>

                                    <div className="bg-light p-3 mb-3">
                                        <FilterBar
                                            value={searchPettyCash}
                                            onChange={setSearchPettyCash}
                                            onClear={() => setSearchPettyCash('')}
                                            placeholder="Search petty cash..."
                                        />
                                    </div>

                                    {/* All Payouts View */}
                                    {pettyCashSubTab === 'all-payouts' && (
                                        <div className="table-responsive">
                                            <table className="table table-bordered align-items-center mb-0">
                                                <thead>
                                                    <tr>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid By</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Paid to Entity</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Exp. Type</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Mode</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-end">Amount (₹)</th>
                                                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allPayouts.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4">No payouts found.</td>
                                                        </tr>
                                                    ) : (
                                                        allPayouts.map((item) => (
                                                            <tr key={item.id}>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0 px-3">{item.paymentDate ? item.paymentDate.split('T')[0] : 'N/A'}</p>
                                                                </td>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                        {item.createdByName || 'N/A'}
                                                                    </p>
                                                                </td>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                        {item.staffName || item.memberName || item.vendorName || 'N/A'}
                                                                    </p>
                                                                    <p className="text-xs text-secondary mb-0">{item.projectName || 'No Project'}</p>
                                                                </td>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0">{item.expenditureTypeName || 'N/A'}</p>
                                                                    <p className="text-xs text-secondary mb-0">{item.remarks || ''}</p>
                                                                </td>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0">{item.paymentMode || 'N/A'}</p>
                                                                    <p className="text-xs text-secondary mb-0">{item.transactionNo || ''}</p>
                                                                </td>
                                                                <td>
                                                                    <p className="text-xs font-weight-bold mb-0 text-end">₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                                                </td>
                                                                <td>
                                                                    <span className={`badge badge-sm bg-gradient-${item.isActive ? 'success' : 'secondary'}`}>
                                                                        {item.isActive ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Group by Date View */}
                                    {pettyCashSubTab === 'group-date' && (
                                        <div>
                                            {Object.entries(groupByDate(allPayouts)).map(([date, items]) => {
                                                const groupKey = `date-${date}`;
                                                const isExpanded = expandedGroups[groupKey];
                                                return (
                                                    <div key={date} className="mb-3">
                                                        <div
                                                            className="bg-light p-3 rounded d-flex justify-content-between align-items-center cursor-pointer"
                                                            onClick={() => toggleGroup(groupKey)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center gap-3">
                                                                <i className={`material-symbols-rounded ${isExpanded ? '' : 'collapsed'}`} style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.3s' }}>
                                                                    expand_more
                                                                </i>
                                                                <h6 className="mb-0 font-weight-bold">{date}</h6>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <span className="text-sm text-secondary">{items.length} transaction(s)</span>
                                                                <span className="badge bg-gradient-info">₹{calculateGroupTotal(items).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="table-responsive mt-2">
                                                                <table className="table table-bordered align-items-center mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid By</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid to Entity</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Exp. Type</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Mode</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end">Amount (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {items.map((item) => (
                                                                            <tr key={item.id}>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                                        {item.createdByName || 'N/A'}
                                                                                    </p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                                        {item.staffName || item.memberName || item.vendorName || 'N/A'}
                                                                                    </p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">{item.expenditureTypeName || 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">{item.paymentMode || 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0 text-end">₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Group by Entity View */}
                                    {pettyCashSubTab === 'group-entity' && (
                                        <div>
                                            {Object.entries(groupByEntity(allPayouts)).map(([entity, items]) => {
                                                const groupKey = `entity-${entity}`;
                                                const isExpanded = expandedGroups[groupKey];
                                                return (
                                                    <div key={entity} className="mb-3">
                                                        <div
                                                            className="bg-light p-3 rounded d-flex justify-content-between align-items-center cursor-pointer"
                                                            onClick={() => toggleGroup(groupKey)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center gap-3">
                                                                <i className={`material-symbols-rounded ${isExpanded ? '' : 'collapsed'}`} style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.3s' }}>
                                                                    expand_more
                                                                </i>
                                                                <h6 className="mb-0 font-weight-bold">{entity}</h6>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <span className="text-sm text-secondary">{items.length} transaction(s)</span>
                                                                <span className="badge bg-gradient-info">₹{calculateGroupTotal(items).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="table-responsive mt-2">
                                                                <table className="table table-bordered align-items-center mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid By</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Exp. Type</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Mode</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end">Amount (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {items.map((item) => (
                                                                            <tr key={item.id}>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0 px-3">{item.paymentDate ? item.paymentDate.split('T')[0] : 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                                        {item.createdByName || 'N/A'}
                                                                                    </p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">{item.expenditureTypeName || 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">{item.paymentMode || 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0 text-end">₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Group by Expenditure Type View */}
                                    {pettyCashSubTab === 'group-type' && (
                                        <div>
                                            {Object.entries(groupByExpenditure(allPayouts)).map(([type, items]) => {
                                                const groupKey = `type-${type}`;
                                                const isExpanded = expandedGroups[groupKey];
                                                return (
                                                    <div key={type} className="mb-3">
                                                        <div
                                                            className="bg-light p-3 rounded d-flex justify-content-between align-items-center cursor-pointer"
                                                            onClick={() => toggleGroup(groupKey)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center gap-3">
                                                                <i className={`material-symbols-rounded ${isExpanded ? '' : 'collapsed'}`} style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.3s' }}>
                                                                    expand_more
                                                                </i>
                                                                <h6 className="mb-0 font-weight-bold">{type}</h6>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <span className="text-sm text-secondary">{items.length} transaction(s)</span>
                                                                <span className="badge bg-gradient-info">₹{calculateGroupTotal(items).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            </div>
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="table-responsive mt-2">
                                                                <table className="table table-bordered align-items-center mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Date</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid By</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Paid to Entity</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Mode</th>
                                                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-end">Amount (₹)</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {items.map((item) => (
                                                                            <tr key={item.id}>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0 px-3">{item.paymentDate ? item.paymentDate.split('T')[0] : 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                                        {item.createdByName || 'N/A'}
                                                                                    </p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">
                                                                                        {item.staffName || item.memberName || item.vendorName || 'N/A'}
                                                                                    </p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0">{item.paymentMode || 'N/A'}</p>
                                                                                </td>
                                                                                <td>
                                                                                    <p className="text-xs font-weight-bold mb-0 text-end">₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetails;